import React, { useState, useEffect } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  submitDamageClaim, getEligibleBookingsForDamageClaim, getDisputeConfig,
} from '../../services/disputeService';
import PhotoUploader from '../../components/PhotoUploader';
import { ACCEPTED_IMAGE_TYPES, ACCEPTED_ALL_TYPES } from '../../services/storageService';
import toast from 'react-hot-toast';

export default function SubmitDamageClaim() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();

  const prefilledBookingId = location.state?.bookingId || null;

  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    bookingId: prefilledBookingId || '',
    type: 'damage',
    description: '',
    amount: '',
    photos: [],         // damage photos
    receiptPhotos: [],  // receipts/invoices
  });

  useEffect(() => {
    if (!userProfile?.id) return;
    getEligibleBookingsForDamageClaim(userProfile.id)
      .then(setBookings)
      .catch((err) => toast.error(err.message || 'Could not load trips'))
      .finally(() => setBookingsLoading(false));
  }, [userProfile?.id]);

  useEffect(() => { getDisputeConfig().then(setConfig).catch(() => {}); }, []);

  const cfg = config?.damageClaim || {
    MIN_PHOTOS: 3, MAX_PHOTOS: 10, MAX_RECEIPT_PHOTOS: 5,
    MIN_AMOUNT: 1, MAX_AMOUNT: 5000, SUBMISSION_DEADLINE_HOURS: 48,
  };

  const handleSubmit = async () => {
    if (!form.bookingId) { toast.error('Select a trip'); return; }
    if (!form.description || form.description.trim().length < 50) {
      toast.error('Description must be at least 50 characters');
      return;
    }
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt < cfg.MIN_AMOUNT || amt > cfg.MAX_AMOUNT) {
      toast.error(`Amount must be between $${cfg.MIN_AMOUNT} and $${cfg.MAX_AMOUNT}`);
      return;
    }
    if (form.photos.length < cfg.MIN_PHOTOS) {
      toast.error(`Minimum ${cfg.MIN_PHOTOS} damage photos required`);
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitDamageClaim({
        bookingId: form.bookingId,
        type: form.type,
        description: form.description.trim(),
        amount: amt,
        stagedPhotoUrls: form.photos.map(p => p.url),
        stagedReceiptUrls: form.receiptPhotos.map(p => p.url),
      });
      toast.success(`Claim ${result.referenceNumber} submitted. Admin will review within 48 hours.`);
      navigate(`/driver/claims/${result.claimId}`);
    } catch (err) {
      toast.error(err.message || 'Submission failed');
    }
    setSubmitting(false);
  };

  const selectedBooking = bookings.find(b => b.id === form.bookingId);

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate(-1)} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Back</button>
      </div>

      <h1 className="font-serif mt-0 mb-2">Submit damage / cleaning claim</h1>
      <p className="text-body text-[0.9rem] mb-4">
        Claims must be submitted within {cfg.SUBMISSION_DEADLINE_HOURS} hours of trip completion.
        Admin will review and decide. If approved, the rider is charged and you receive 100% of the approved amount.
      </p>

      {/* Trip selector */}
      <div className="form-group">
        <label className="form-label">From which trip? *</label>
        {bookingsLoading ? (
          <div className="p-3 text-muted text-[0.9rem]">Loading recent trips...</div>
        ) : bookings.length === 0 ? (
          <div className="p-3.5 rounded-[10px] text-[0.85rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
            No eligible trips. Damage claims must be filed within {cfg.SUBMISSION_DEADLINE_HOURS} hours of a completed trip.
          </div>
        ) : (
          <select className="form-input form-select" value={form.bookingId}
            onChange={(e) => setForm({ ...form, bookingId: e.target.value })}>
            <option value="">Select a trip...</option>
            {bookings.map(b => {
              const completedAt = b.completedAt?.toDate?.() || new Date(b.completedAt);
              return (
                <option key={b.id} value={b.id}>
                  {b.referenceNumber || b.id.substring(0, 8)} — {completedAt.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} — {b.riderName || 'Rider'}
                </option>
              );
            })}
          </select>
        )}
        {selectedBooking && (
          <div className="p-2.5 rounded-lg bg-slate-50 mt-1.5 text-[0.82rem] text-body">
            Rider: <strong>{selectedBooking.riderName || 'Rider'}</strong> · Fare ${selectedBooking.fareBreakdown?.totalFare?.toFixed(2) || '—'}
          </div>
        )}
      </div>

      {/* Type */}
      <div className="form-group">
        <label className="form-label">Claim type *</label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'damage', icon: '🔧', title: 'Damage', desc: 'Physical damage to vehicle' },
            { value: 'cleaning', icon: '🧽', title: 'Cleaning', desc: 'Required cleaning beyond normal' },
          ].map(opt => (
            <label key={opt.value} className="block p-3 rounded-[10px] cursor-pointer"
              style={{
                border: `1.5px solid ${form.type === opt.value ? '#2dd4a8' : '#e2e8f0'}`,
                background: form.type === opt.value ? 'rgba(45,212,168,0.05)' : '#fff',
              }}>
              <input type="radio" name="type" value={opt.value}
                checked={form.type === opt.value}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="mr-2" />
              <span className="text-[1.2rem] mr-1.5">{opt.icon}</span>
              <strong className="text-[0.9rem] text-brand-dark">{opt.title}</strong>
              <div className="text-[0.75rem] text-body mt-0.5 ml-7">
                {opt.desc}
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Description */}
      <div className="form-group">
        <label className="form-label">Detailed description * (min 50 chars)</label>
        <textarea className="form-input" rows={5}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Describe exactly what happened, what was damaged or needs cleaning, the extent of the issue, and any actions you've already taken (e.g. 'Rider spilled red wine on rear seat fabric. Tried to clean immediately but stain remains. Took to professional cleaner who quoted...')"
        />
        <div className="text-[0.75rem] text-muted mt-1">
          {form.description.length} characters
        </div>
      </div>

      {/* Amount */}
      <div className="form-group">
        <label className="form-label">Claim amount (AUD) *</label>
        <input type="number" className="form-input"
          step="0.01" min={cfg.MIN_AMOUNT} max={cfg.MAX_AMOUNT}
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          placeholder="e.g. 85.00" />
        <div className="text-[0.75rem] text-muted mt-1">
          Min ${cfg.MIN_AMOUNT}, max ${cfg.MAX_AMOUNT}. Admin may approve a lower amount.
        </div>
      </div>

      {/* Damage photos */}
      <PhotoUploader
        userId={userProfile?.id}
        value={form.photos}
        onChange={(photos) => setForm({ ...form, photos })}
        maxFiles={cfg.MAX_PHOTOS}
        minFiles={cfg.MIN_PHOTOS}
        acceptedTypes={ACCEPTED_IMAGE_TYPES}
        label="Damage photos"
        required
        helperText={`Photographic evidence is required. Show the damage clearly from multiple angles. Min ${cfg.MIN_PHOTOS}, max ${cfg.MAX_PHOTOS}.`}
      />

      {/* Receipts */}
      <PhotoUploader
        userId={userProfile?.id}
        value={form.receiptPhotos}
        onChange={(receiptPhotos) => setForm({ ...form, receiptPhotos })}
        maxFiles={cfg.MAX_RECEIPT_PHOTOS}
        acceptedTypes={ACCEPTED_ALL_TYPES}
        label="Receipts / invoices (optional)"
        helperText={`Upload receipts or quotes supporting your claim amount. Images or PDFs. Max ${cfg.MAX_RECEIPT_PHOTOS}.`}
      />

      {/* What happens next */}
      <div className="p-3.5 rounded-xl mb-4 text-[0.85rem] leading-relaxed" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
        <strong>What happens next:</strong>
        <ul className="mt-1.5 mb-0 pl-5">
          <li>Admin reviews your claim (typically within 48 hours).</li>
          <li>If approved, the rider is charged the approved amount via Stripe.</li>
          <li>You receive 100% of the approved amount (no Reservo commission).</li>
          <li>If rejected or you withdraw, no charge occurs.</li>
        </ul>
      </div>

      <button onClick={handleSubmit}
        disabled={submitting || bookings.length === 0 || form.photos.length < cfg.MIN_PHOTOS}
        className="w-full p-3.5 rounded-xl border-0 bg-brand-primary text-white cursor-pointer font-bold text-[1rem]"
        style={{ opacity: (submitting || bookings.length === 0 || form.photos.length < cfg.MIN_PHOTOS) ? 0.6 : 1 }}>
        {submitting ? 'Submitting...' : 'Submit claim'}
      </button>
      <BackButtonBottom label="Back" />
    </div>
  );
}